import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import {
  DataGrid,
  FormattedFloatInput,
  getFormattedPercentage,
  GridColDef,
  ListHeader,
  MenuButton,
  ModalOpenButton,
  Select,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { head } from "lodash-es";
import React from "react";
import { useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { useFeature } from "../../../common/FeatureFlags.js";
import { usePaginationParamsInUrl } from "../../commons/pagination/usePaginationParams.js";
import { ViewMode, ViewModeMenuItem } from "../../commons/ViewModeMenuItem.js";
import {
  MEDIUM_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import { useDiscountGroupTypes } from "../../features/crm-companies/hooks/useDiscountGroupTypes.js";
import { AddPimSupplierDiscountGroupsImportationButton } from "../../features/pimImportations/buttons/AddPimSupplierDiscountGroupsImportationButton.js";
import { SupplierDiscountGroupVariantCreateMenuButton } from "../../features/products/buttons/SupplierDiscountGroupVariantCreateMenuButton.js";
import { SupplierDiscountGroupVariantDeleteMenuButton } from "../../features/products/buttons/SupplierDiscountGroupVariantDeleteMenuButton.js";
import { SupplierDiscountGroupVariantCreateModal } from "../../features/products/modals/SupplierDiscountGroupVariantCreateModal.js";
import {
  DiscountGroupFragment,
  usePimModifySupplierDiscountGroupMutation,
  useSupplierOwnInformationDiscountGroupsQuery,
  useSupplierOwnInformationDiscountGroupsVariantQuery,
  useSupplierOwnInformationDiscountGroupVariantsQuery,
} from "./SupplierDiscountGroups.generated.js";

interface Props {}

export function SupplierDiscountGroups({ ...props }: Props) {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const { t } = useTranslate(["CrmOrganisations"]);

  const isSupplierDiscountGroupVariantsEnabled = useFeature(
    "SupplierDiscountGroupVariants"
  );

  return (
    <Box>
      <DiscountGroupsSection supplierId={supplierId} />

      {isSupplierDiscountGroupVariantsEnabled && (
        <DiscountGroupsVariantSection supplierId={supplierId} />
      )}
    </Box>
  );
}

function DiscountGroupsSection({ supplierId }: { supplierId: string }) {
  const { t } = useTranslate(["CrmOrganisations", "Global"]);
  const language = useTolgee(["language"]).getLanguage()!;

  const [viewMode, setViewMode] = React.useState<ViewMode>(null);

  const { offset, limit, paginationModel, setPaginationModel } =
    usePaginationParamsInUrl({
      pageUrlParam: "pageDiscountGroups",
      perPageUrlParam: "pageSizeDiscountGroups",
      perPageDefaultAmount: MEDIUM_LIST_RESULTS_PER_PAGE,
      base: 0,
    });

  const { discountGroupTypeLabels } = useDiscountGroupTypes();

  const client = useApolloClient();

  const query = useSupplierOwnInformationDiscountGroupsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      supplierId,
      offset,
      limit,
    },
  });

  const items =
    getDataOrNull(query.data?.pimGetSupplierDiscountGroups)?.discountGroups ??
    [];
  const total =
    getDataOrNull(query.data?.pimGetSupplierDiscountGroups)?.totalCount ?? 0;

  return (
    <>
      <ListHeader
        ActionButtons={
          <MenuButton>
            <ViewModeMenuItem
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              allowedModes={[null, "edit"]}
            />
          </MenuButton>
        }
        marginBottom={0.5}
      />
      <DataGrid
        density="compact"
        loading={query.loading}
        hideFooter={total === 0}
        columns={
          [
            {
              field: "key",
              headerName: t("Shortcut", {
                ns: "Global",
              }),
              flex: 1,
              minWidth: 30,
              sortable: false,
              valueGetter: (value, row) => row.key,
            },
            {
              field: "description",
              headerName: t("Name", {
                ns: "Global",
              }),
              flex: 2,
              minWidth: 50,
              sortable: false,
              valueGetter: (value, row) => row.description,
            },
            {
              field: "value",
              headerName: t("Value", {
                ns: "Global",
              }),
              flex: 1,
              minWidth: 30,
              sortable: false,
              headerAlign: "right",
              align: "right",
              valueGetter: (value, row) => row.value,
              renderCell: ({ row }) =>
                viewMode === "edit" ? (
                  <DiscountGroupValueInput
                    discountGroup={row}
                    supplierId={supplierId}
                  />
                ) : (
                  getFormattedPercentage(row.value, language)
                ),
            },
            {
              field: "discountType",
              headerName: t("Type", {
                ns: "Global",
              }),
              flex: 1.25,
              minWidth: 50,
              sortable: false,
              headerAlign: "right",
              align: "right",
              valueGetter: (value, row) => row.discountType,
              renderCell: ({ row }) =>
                discountGroupTypeLabels[row.discountType],
            },
          ] as GridColDef<DiscountGroupFragment>[]
        }
        rows={items}
        paginationModel={paginationModel}
        onPaginationModelChange={newPaginationModel => {
          setPaginationModel(newPaginationModel);
        }}
        pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
        rowCount={total}
      />
    </>
  );
}

function DiscountGroupsVariantSection({ supplierId }: { supplierId: string }) {
  const { t } = useTranslate(["CrmOrganisations", "Global"]);
  const language = useTolgee(["language"]).getLanguage()!;

  const [viewMode, setViewMode] = React.useState<ViewMode>(null);

  const [supplierDiscountGroupVariantId, setSupplierDiscountGroupVariantId] =
    React.useState<string | null>(null);

  const { offset, limit, paginationModel, setPaginationModel } =
    usePaginationParamsInUrl({
      pageUrlParam: "pageDiscountGroupsVariant",
      perPageUrlParam: "pageSizeDiscountGroupsVariant",
      perPageDefaultAmount: MEDIUM_LIST_RESULTS_PER_PAGE,
      base: 0,
    });

  const { discountGroupTypeLabels } = useDiscountGroupTypes();

  const client = useApolloClient();

  const supplierDiscountGroupVariantsQuery =
    useSupplierOwnInformationDiscountGroupVariantsQuery({
      client,
      fetchPolicy: "network-only",
      variables: {
        supplierId,
        offset: 0,
        limit: 100,
      },
    });

  const supplierDiscountGroupsVariantQuery =
    useSupplierOwnInformationDiscountGroupsVariantQuery({
      client,
      fetchPolicy: "network-only",
      variables: {
        supplierDiscountGroupVariantId: supplierDiscountGroupVariantId!,
        offset,
        limit,
      },
      skip: !supplierDiscountGroupVariantId,
    });

  const supplierDiscountGroupVariants =
    getDataOrNull(
      supplierDiscountGroupVariantsQuery.data
        ?.pimSearchSupplierDiscountGroupVariants
    )?.supplierDiscountGroupVariants ?? [];

  const supplierDiscountGroupsVariantItems =
    getDataOrNull(
      supplierDiscountGroupsVariantQuery.data?.pimGetSupplierDiscountGroups
    )?.discountGroups ?? [];
  const supplierDiscountGroupsVariantTotal =
    getDataOrNull(
      supplierDiscountGroupsVariantQuery.data?.pimGetSupplierDiscountGroups
    )?.totalCount ?? 0;

  React.useEffect(() => {
    const supplierDiscountGroupVariant = head(supplierDiscountGroupVariants);
    if (!supplierDiscountGroupVariantId && supplierDiscountGroupVariant) {
      setSupplierDiscountGroupVariantId(supplierDiscountGroupVariant.id);
    }
  }, [supplierDiscountGroupVariantId, supplierDiscountGroupVariants]);

  return (
    <Stack direction="column" spacing={1}>
      <Divider />
      <Stack
        direction="row"
        spacing={1}
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h4">
          {t("Discount group variants", {
            ns: "CrmOrganisations",
          })}
        </Typography>
        <MenuButton>
          <ViewModeMenuItem
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            allowedModes={[null, "edit"]}
          />
          {!!supplierDiscountGroupVariantId && (
            <AddPimSupplierDiscountGroupsImportationButton
              supplierId={supplierId}
              supplierDiscountGroupVariantId={supplierDiscountGroupVariantId}
              refetchQueries={[
                namedOperations.Query
                  .SupplierOwnInformationDiscountGroupVariants,
              ]}
            />
          )}
          <SupplierDiscountGroupVariantCreateMenuButton
            supplierId={supplierId}
            refetchQueries={[
              namedOperations.Query.SupplierOwnInformationDiscountGroupVariants,
            ]}
          />
          {!!supplierDiscountGroupVariantId && (
            <SupplierDiscountGroupVariantDeleteMenuButton
              supplierDiscountGroupVariantId={supplierDiscountGroupVariantId}
              refetchQueries={[
                namedOperations.Query
                  .SupplierOwnInformationDiscountGroupVariants,
              ]}
            />
          )}
        </MenuButton>
      </Stack>
      <Stack flexDirection="row">
        <Select
          label={t("Variant", {
            ns: "CrmOrganisations",
          })}
          // placeholder={t("Variant", {
          //   ns: "CrmOrganisations",
          // })}
          size="small"
          options={supplierDiscountGroupVariants.map(v => ({
            value: v.id,
            label: v.title,
          }))}
          value={supplierDiscountGroupVariantId}
          onChange={value => {
            setSupplierDiscountGroupVariantId(value);
          }}
        />
        <ModalOpenButton
          Modal={SupplierDiscountGroupVariantCreateModal}
          modalProps={{ supplierId }}
        >
          <IconButton color="primary">
            <AddIcon />
          </IconButton>
        </ModalOpenButton>
      </Stack>

      <DataGrid
        density="compact"
        loading={supplierDiscountGroupsVariantQuery.loading}
        hideFooter={supplierDiscountGroupsVariantTotal === 0}
        columns={
          [
            {
              field: "key",
              headerName: t("Shortcut", {
                ns: "Global",
              }),
              flex: 1,
              minWidth: 30,
              sortable: false,
              valueGetter: (value, row) => row.key,
            },
            {
              field: "description",
              headerName: t("Name", {
                ns: "Global",
              }),
              flex: 2,
              minWidth: 50,
              sortable: false,
              valueGetter: (value, row) => row.description,
            },
            {
              field: "value",
              headerName: t("Value", {
                ns: "Global",
              }),
              flex: 1,
              minWidth: 30,
              sortable: false,
              headerAlign: "right",
              align: "right",
              valueGetter: (value, row) => row.value,
              renderCell: ({ row }) =>
                viewMode === "edit" ? (
                  <DiscountGroupValueInput
                    discountGroup={row}
                    supplierId={supplierId}
                  />
                ) : (
                  getFormattedPercentage(row.value, language)
                ),
            },
            {
              field: "discountType",
              headerName: t("Type", {
                ns: "Global",
              }),
              flex: 1.25,
              minWidth: 50,
              sortable: false,
              headerAlign: "right",
              align: "right",
              valueGetter: (value, row) => row.discountType,
              renderCell: ({ row }) =>
                discountGroupTypeLabels[row.discountType],
            },
          ] as GridColDef<DiscountGroupFragment>[]
        }
        rows={supplierDiscountGroupsVariantItems}
        paginationModel={paginationModel}
        onPaginationModelChange={newPaginationModel => {
          setPaginationModel(newPaginationModel);
        }}
        pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
        rowCount={supplierDiscountGroupsVariantTotal}
      />
    </Stack>
  );
}

function DiscountGroupValueInput({
  discountGroup,
  supplierId,
}: {
  discountGroup: DiscountGroupFragment;
  supplierId: string;
}) {
  const client = useApolloClient();
  const [value, setValue] = React.useState<number>(discountGroup.value);
  const [modifyDiscountGroup] = usePimModifySupplierDiscountGroupMutation({
    client,
  });
  const handleChange = async () => {
    if (value !== discountGroup.value) {
      await modifyDiscountGroup({
        variables: {
          input: {
            discountGroupId: discountGroup.id,
            supplierId,
            args: {
              value,
            },
          },
        },
      });
    }
  };
  return (
    <FormattedFloatInput
      value={value}
      onChange={v => {
        if (v !== null) setValue(v);
      }}
      min={0}
      max={discountGroup.discountType === "DISCOUNT" ? 100 : undefined}
      unit={"%"}
      multiplier={100}
      size="extra-small"
      onBlur={handleChange}
      onKeyPress={async e => {
        e.stopPropagation();
        if (e.key === "Enter") {
          await handleChange();
        }
      }}
      onKeyDown={e => {
        e.stopPropagation();
      }}
    />
  );
}
